/** 手机使用要求：记录按字段阅读，金额/薪资对比保留横向表；弹窗表单可换行、主要操作便于触控。只调整显示，不改变数据、权限及审批规则。 */
import React, { useEffect, useState } from "react";
import { useApp } from "../../../context/AppContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../../components/ui/dialog";
import { toast } from "../../../components/ui/sonner";
import { Loader2, Mail } from "lucide-react";
import { errMsg } from "./cnShared";
import { fmtD } from "../../../lib/date";

export const CnEmailDialog = ({ month, zh, onClose, onSent }) => {
  const { api } = useApp();
  const T = (z, e) => (zh ? z : e);
  const [prev, setPrev] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.post("/hrms/cn-payroll/runs/email", { month, dryRun: true }).then(({ data }) => setPrev(data))
      .catch((e) => { toast.error(errMsg(e, T("预览失败", "Preview failed"))); onClose(); });
  }, [api, month]); // eslint-disable-line

  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/hrms/cn-payroll/runs/email", { month });
      setResult(data);
      toast.success(T(`已发送 ${data.sent} 封，失败 ${data.failed.length}`, `${data.sent} sent, ${data.failed.length} failed`));
      onSent();
    } catch (e) { toast.error(errMsg(e, T("发送失败", "Send failed"))); }
    setBusy(false);
  };
  const resent = prev?.recipients?.filter((r) => r.emailedAt).length || 0;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="flex items-center gap-1.5"><Mail className="h-4 w-4 text-emerald-600" />{T("邮件发送工资单", "Email payslips")} · {month}</DialogTitle></DialogHeader>
        {!prev ? <div className="py-6 text-center"><Loader2 className="h-5 w-5 animate-spin inline text-slate-300" /></div> : (
          <div className="space-y-3 text-sm" data-testid="hrms-cn-email-dlg">
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2"><p className="text-slate-500">{T("将发送（已确认 · 有邮箱）", "Will send (confirmed · has email)")}</p><p className="text-lg font-extrabold text-emerald-700" data-testid="hrms-cn-email-will">{prev.wouldSend}</p></div>
              <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2"><p className="text-slate-500">{T("跳过", "Skipped")}</p><p className="text-lg font-extrabold text-slate-700" data-testid="hrms-cn-email-skip">{prev.skipped.length}</p></div>
            </div>
            {prev.recipients.length > 0 && <ul className="max-h-32 overflow-y-auto text-xs text-slate-600 space-y-0.5" data-testid="hrms-cn-email-list">
              {prev.recipients.map((r) => <li key={r.runId} className="flex justify-between gap-2"><span>{r.name}</span><span className="text-slate-400 truncate">{r.to}{r.emailedAt ? T(`（已发 ${fmtD(r.emailedAt)}，将重发）`, ` (sent ${fmtD(r.emailedAt)}, resend)`) : ""}</span></li>)}
            </ul>}
            {prev.skipped.length > 0 && <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="hrms-cn-email-skipped">{T("跳过：", "Skipped: ")}{prev.skipped.map((s) => `${s.name}（${s.reason}）`).join("、")}</p>}
            {!prev.mailConfigured && <p className="text-[11px] text-red-600" data-testid="hrms-cn-email-noconf">{T("邮件服务未配置，无法发送。", "Email service not configured.")}</p>}
            {resent > 0 && <p className="text-[11px] text-slate-500">{T(`${resent} 人此前已发送过，确认后会再次发送。`, `${resent} were emailed before and will be re-sent.`)}</p>}
            {result ? (
              <div className="text-xs rounded-lg bg-slate-50 border border-slate-200 px-3 py-2" data-testid="hrms-cn-email-result">
                <p className="text-emerald-700 font-semibold">{T(`已发送 ${result.sent}：`, `Sent ${result.sent}: `)}{result.sentNames.join("、") || "-"}</p>
                {result.failed.length > 0 && <p className="text-red-600 mt-1">{T("失败：", "Failed: ")}{result.failed.map((f) => `${f.name}（${f.reason}）`).join("、")}</p>}
              </div>
            ) : <p className="text-[11px] text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">{T("⚠️ 邮件将真实发出到员工工作邮箱，请确认工资数据无误。", "⚠️ Emails will really be sent to employees' work addresses.")}</p>}
            <div className="flex justify-end gap-2">
              <button onClick={onClose} className="text-xs rounded-full border border-slate-200 px-4 py-2">{result ? T("关闭", "Close") : T("取消", "Cancel")}</button>
              {!result && <button data-testid="hrms-cn-email-confirm" disabled={busy || !prev.wouldSend || !prev.mailConfigured} onClick={send}
                className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-xs rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-4 py-2 disabled:opacity-50">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin inline" /> : T(`确认发送 ${prev.wouldSend} 封`, `Send ${prev.wouldSend}`)}</button>}
            </div>
          </div>)}
      </DialogContent>
    </Dialog>
  );
};
